import { canAccessAnyBusinessFunction } from './canAccessResource';

/**
 * @param {import('./types/authz.types').AuthzContext} context
 * @param {import('./types/authz.types').AuthzRequirement} requirement
 */
function evaluateSingle(context, requirement) {
  if (requirement == null) {
    return true;
  }

  if (requirement.policy === 'super_admin_only') {
    return context.isFullAccess;
  }

  if (requirement.policy === 'business_functions_scoped') {
    return canAccessAnyBusinessFunction(context);
  }

  if (requirement.feature) {
    const feat = requirement.feature;
    const featLower = typeof feat === 'string' ? feat.toLowerCase() : feat;
    return Boolean(
      context.grants.features?.[feat]
      || context.grants.features?.[featLower]
      || context.user?.profile?.permissions?.[feat]
      || context.user?.profile?.permissions?.[featLower]
      || context.user?.permissions?.[feat]
      || context.user?.permissions?.[featLower]
    );
  }

  if (requirement.object && requirement.action) {
    const modelKey = requirement.object.toLowerCase();
    return Boolean(context.grants.objects?.[modelKey]?.[requirement.action]);
  }

  return false;
}

/**
 * @param {import('./types/authz.types').AuthzContext} context
 * @param {import('./types/authz.types').AuthzRequirement} requirement
 */
export function canAccess(context, requirement) {
  if (!context) {
    return false;
  }

  if (context.isFullAccess) {
    return true;
  }

  if (!context.isAuthzV2Enabled) {
    return true;
  }

  if (requirement == null) {
    return true;
  }

  if (requirement.anyOf?.length) {
    return requirement.anyOf.some((item) => evaluateSingle(context, item));
  }

  if (requirement.allOf?.length) {
    return requirement.allOf.every((item) => evaluateSingle(context, item));
  }

  return evaluateSingle(context, requirement);
}

/**
 * @param {import('./types/authz.types').AuthzContext} context
 * @param {import('./types/authz.types').AuthzRequirement[]} requirements
 */
export function canAccessAny(context, requirements = []) {
  if (!requirements.length) {
    return false;
  }
  return requirements.some((requirement) => canAccess(context, requirement));
}
